
import React from "react";
import { MapPin, RefreshCw, Info } from "lucide-react";
import { DealerCard } from "@/components/DealerCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Dealer } from "@/types/dealer";
import { useGeolocation } from "@/hooks/useGeolocation";
import { calculateDistance, formatDistance } from "@/lib/geolocation";
import { Button } from "@/components/ui/button";
import { InfiniteScrollLoader } from "./InfiniteScrollLoader";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DealersListViewProps {
  dealers: Dealer[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  onDealerClick: (dealerId: string) => void;
  onRefresh?: () => void;
  onLoadMore?: () => void;
  enableInfiniteScroll?: boolean;
}

export function DealersListView({
  dealers,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  totalCount = 0,
  onDealerClick,
  onRefresh,
  onLoadMore,
  enableInfiniteScroll = false
}: DealersListViewProps) {
  const { coordinates } = useGeolocation({
    showSuccessToast: false
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-3">
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (dealers.length === 0) {
    return (
      <div className="text-center py-12">
        <MapPin className="mx-auto h-12 w-12 text-muted-foreground opacity-20" />
        <h3 className="mt-4 text-lg font-medium">Nessun compro oro trovato</h3>
        <p className="mt-2 text-muted-foreground">Prova a modificare i filtri di ricerca</p>
      </div>
    );
  }

  // Prepara dealers per visualizzazione con distanza
  const displayDealers = dealers.map(dealer => {
    let distance = undefined;
    if (coordinates) {
      const distanceInKm = calculateDistance(
        coordinates.latitude,
        coordinates.longitude,
        dealer.latitude,
        dealer.longitude
      );
      distance = formatDistance(distanceInKm);
    }
    return { ...dealer, distance };
  });

  return (
    <div className="space-y-6">
      {/* Header con contatori e azioni */}
      <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground border-b pb-2">
        <div className="flex items-center gap-2">
          <span>
            {enableInfiniteScroll && totalCount > 0 
              ? `Visualizzati ${dealers.length} di ${totalCount} compro oro`
              : `${dealers.length} compro oro trovati`
            }
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Info className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {enableInfiniteScroll 
                    ? "Scorri verso il basso per caricare altri compro oro"
                    : "Lista completa dei compro oro disponibili"
                  }
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {onRefresh && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onRefresh}
            className="flex items-center gap-1 text-xs"
          >
            <RefreshCw className="h-3 w-3" />
            Aggiorna
          </Button>
        )}
      </div>
      
      {/* Griglia dealer cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 dealer-cards-grid">
        {displayDealers.map((dealer) => (
          <button 
            key={dealer.id} 
            onClick={() => onDealerClick(dealer.id)} 
            className="text-left w-full h-full"
          >
            <DealerCard
              id={dealer.id}
              name={dealer.name}
              address={dealer.address}
              rating={dealer.rating}
              rating_count={dealer.rating_count} // Pass rating_count
              is_verified={dealer.is_verified || false}
              activated={dealer.activated || false} 
              isFavorite={dealer.isFavorite}
              buySpread={dealer.buySpread}
              image_url={dealer.image_url}
              postal_code={dealer.postal_code}
              phone={dealer.phone}
              openingHours={dealer.opening_hours}
              distance={dealer.distance}
              city={dealer.city}
              className="h-full"
            />
          </button>
        ))}
      </div>

      {/* Infinite scroll loader */}
      {enableInfiniteScroll && onLoadMore && (
        <InfiniteScrollLoader
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
          threshold={300}
        />
      )}
    </div>
  );
}
